import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { apiClient } from '@/lib/api/client';
import { publicApiClient } from '@/lib/api/publicClient';
import { useTenantStore } from '@/stores/tenantStore';

import { usePublicRun } from '../publicRun';

const templatePreviewSchema = z.object({
  /** Base64 of the rendered PDF. */
  documentBase64: z.string(),
  fileName: z.string(),
  /** Labels of fields no earlier step supplied a value for. */
  unresolvedFields: z.array(z.string()).default([]),
});

export type TemplatePreview = z.infer<typeof templatePreviewSchema>;

/**
 * The document this step will generate, rendered with the values earlier steps have
 * verified.
 *
 * Rendering is free — SignCare's preview endpoint bills nothing — so this runs on
 * entering the step rather than behind a button. It is NOT retried automatically:
 * a failure here means the template or its bindings need fixing, and hammering the
 * gateway would not change that.
 */
export function useTemplatePreview(sessionId: string | undefined, stepId: string) {
  const tenantId = useTenantStore((s) => s.tenantId);
  const publicRun = usePublicRun();

  return useQuery({
    queryKey: ['template-preview', publicRun?.token ?? `${tenantId}:${sessionId}`, stepId],
    queryFn: async ({ signal }) => {
      const path = publicRun
        ? `api/v1/public/sessions/${publicRun.token}/steps/${stepId}/template-preview`
        : `api/v1/t/${tenantId}/sessions/${sessionId}/steps/${stepId}/template-preview`;
      const client = publicRun ? publicApiClient : apiClient;

      // Rendering a Word document to PDF server-side is slower than a JSON read.
      const raw = await client.get(path, { signal, timeout: 60_000 }).json();
      return templatePreviewSchema.parse(raw);
    },
    enabled: Boolean(stepId) && Boolean(publicRun?.token || (tenantId && sessionId)),
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
